package top.wcpe.allincore.ui.web.asset

internal object AssetBudgetLimits {
    const val TEXT_BYTES: Long = 4L * 1024 * 1024
    const val BINARY_BYTES: Long = 16L * 1024 * 1024
    const val GENERATION_BYTES: Long = 64L * 1024 * 1024
    const val PEAK_BYTES: Long = 128L * 1024 * 1024
    const val MAX_INFLIGHT: Int = 8
}

internal sealed interface AssetBudgetKey {
    data class Resource(val canonicalPath: String, val contentVersion: String) : AssetBudgetKey

    data class InlineTexture(val bbmodelCanonicalPath: String, val inlineIndex: Int, val contentHash: String) :
        AssetBudgetKey
}

internal class AssetBudget(currentBytes: Long = 0) {
    private val countedKeys = mutableSetOf<AssetBudgetKey>()
    private val knownReservations = mutableMapOf<GenerationBudgetKey, KnownReservationState>()
    private var generationId = 1L
    private var currentBytesValue = validCurrentBytes(currentBytes)
    private var previousCurrentBytes = 0L
    private var generationPublished = false
    private var disposed = false

    val currentBytes: Long
        get() = currentBytesValue
    var generationBytes: Long = 0
        private set
    var inflightBytes: Long = 0
        private set
    var inflightCount: Int = 0
        private set

    val peakBytes: Long
        get() = currentBytesValue + generationBytes + inflightBytes

    fun startGeneration() {
        val publishedBytes = if (generationPublished) generationBytes else 0
        startGeneration(currentBytesValue + publishedBytes)
    }

    fun startGeneration(currentBytes: Long) {
        currentBytesValue = validCurrentBytes(currentBytes)
        generationBytes = 0
        countedKeys.clear()
        generationPublished = false
        generationId += 1
    }

    fun publishGeneration() {
        previousCurrentBytes = currentBytesValue
        generationPublished = true
    }

    fun releasePreviousCurrent() {
        if (disposed) return
        currentBytesValue = (currentBytesValue - previousCurrentBytes).coerceAtLeast(0)
        previousCurrentBytes = 0
    }

    fun dispose() {
        if (disposed) return
        disposed = true
        generationId += 1
        countedKeys.clear()
        knownReservations.clear()
        currentBytesValue = 0
        previousCurrentBytes = 0
        generationBytes = 0
        inflightBytes = 0
        inflightCount = 0
    }

    fun reserve(key: AssetBudgetKey, budgetClass: AssetBudgetClass, declaredBytes: Long?): AssetReservation =
        createReservation(
            key,
            budgetClass,
            declaredBytes,
            known = false,
            contributesGeneration = true,
        )

    fun reserveKnown(key: AssetBudgetKey, budgetClass: AssetBudgetClass, declaredBytes: Long): AssetReservation =
        createReservation(
            key,
            budgetClass,
            declaredBytes,
            known = true,
            contributesGeneration = true,
        )

    fun reserveRetained(key: AssetBudgetKey, budgetClass: AssetBudgetClass, declaredBytes: Long): AssetReservation =
        createReservation(
            key,
            budgetClass,
            declaredBytes,
            known = true,
            contributesGeneration = false,
        )

    internal fun commit(
        reservation: AssetReservation,
        actualBytes: Long,
        actualKey: AssetBudgetKey,
        actualClass: AssetBudgetClass,
    ) {
        if (disposed) failBudget("资产预算已释放")
        if (reservation.generationId != generationId) {
            release(reservation)
            failBudget("资产预算 generation 已失效")
        }
        validateActual(reservation, actualBytes, actualClass)
        if (
            reservation.contributesGeneration &&
            actualKey !in countedKeys &&
            generationBytes + actualBytes > AssetBudgetLimits.GENERATION_BYTES
        ) {
            release(reservation)
            failBudget("候选 generation 资产超过六十四 MiB")
        }
        release(reservation, committed = true)
        if (reservation.contributesGeneration && countedKeys.add(actualKey)) {
            generationBytes += actualBytes
        }
    }

    internal fun cancel(reservation: AssetReservation) {
        if (disposed) return
        release(reservation)
    }

    private fun createReservation(
        key: AssetBudgetKey,
        budgetClass: AssetBudgetClass,
        declaredBytes: Long?,
        known: Boolean,
        contributesGeneration: Boolean,
    ): AssetReservation {
        if (disposed) failBudget("资产预算已释放")
        val reservedBytes = reservationBytes(budgetClass, declaredBytes)
        val knownKey = if (known) GenerationBudgetKey(generationId, key) else null
        val shared = knownKey?.let(knownReservations::get)
        val logicalCharge = when {
            !contributesGeneration || key in countedKeys || shared != null -> 0L
            else -> reservedBytes
        }
        val transientCharge = if (known) logicalCharge else logicalCharge * SOURCE_READ_TRANSIENT_MULTIPLIER
        validateCapacity(key, logicalCharge, transientCharge, !known, contributesGeneration)
        if (knownKey != null) registerKnown(knownKey, transientCharge)
        if (!known) inflightCount += 1
        inflightBytes += transientCharge
        return AssetReservation(
            this,
            key,
            budgetClass,
            reservedBytes,
            transientCharge,
            generationId,
            knownKey,
            countsInflight = !known,
            contributesGeneration = contributesGeneration,
        )
    }

    private fun validateCapacity(
        key: AssetBudgetKey,
        logicalCharge: Long,
        transientCharge: Long,
        countsInflight: Boolean,
        contributesGeneration: Boolean,
    ) {
        if (countsInflight && inflightCount >= AssetBudgetLimits.MAX_INFLIGHT) {
            failBudget("在途资产读取超过八个")
        }
        if (
            contributesGeneration &&
            key !in countedKeys &&
            generationBytes + logicalCharge > AssetBudgetLimits.GENERATION_BYTES
        ) {
            failBudget("候选 generation 资产超过六十四 MiB")
        }
        if (peakBytes + transientCharge > AssetBudgetLimits.PEAK_BYTES) {
            failBudget("current、candidate 与在途缓冲超过一百二十八 MiB")
        }
    }

    private fun validateActual(reservation: AssetReservation, actualBytes: Long, actualClass: AssetBudgetClass) {
        val limit = actualClass.singleFileLimit()
        if (actualBytes !in 0..limit || actualBytes > reservation.reservedBytes) {
            release(reservation)
            failBudget("资产实际读取字节超过预留或单文件预算")
        }
    }

    private fun registerKnown(key: GenerationBudgetKey, chargedBytes: Long) {
        val state = knownReservations.getOrPut(key) { KnownReservationState(chargedBytes) }
        state.references += 1
    }

    private fun release(reservation: AssetReservation, committed: Boolean = false) {
        if (reservation.countsInflight) {
            inflightCount -= 1
            inflightBytes -= reservation.chargedBytes
            return
        }
        reservation.knownKey?.let { releaseKnown(it, committed) }
    }

    private fun releaseKnown(key: GenerationBudgetKey, committed: Boolean) {
        val state = knownReservations[key] ?: return
        if (committed && !state.committed) {
            state.committed = true
            inflightBytes -= state.chargedBytes
        }
        state.references -= 1
        if (state.references > 0) return
        if (!state.committed) inflightBytes -= state.chargedBytes
        knownReservations.remove(key)
    }
}

internal class AssetReservation internal constructor(
    private val owner: AssetBudget,
    internal val key: AssetBudgetKey,
    internal val budgetClass: AssetBudgetClass,
    internal val reservedBytes: Long,
    internal val chargedBytes: Long,
    internal val generationId: Long,
    internal val knownKey: GenerationBudgetKey?,
    internal val countsInflight: Boolean,
    internal val contributesGeneration: Boolean,
) {
    private var active = true

    fun commit(actualBytes: Long, actualKey: AssetBudgetKey = key, actualClass: AssetBudgetClass = budgetClass) {
        requireActive()
        active = false
        owner.commit(this, actualBytes, actualKey, actualClass)
    }

    fun cancel() {
        if (!active) return
        active = false
        owner.cancel(this)
    }

    private fun requireActive() {
        if (!active) failBudget("资产预算预留已结束")
    }
}

internal data class GenerationBudgetKey(val generationId: Long, val key: AssetBudgetKey)

private data class KnownReservationState(
    val chargedBytes: Long,
    var references: Int = 0,
    var committed: Boolean = false,
)

private const val SOURCE_READ_TRANSIENT_MULTIPLIER = 2

private fun reservationBytes(budgetClass: AssetBudgetClass, declaredBytes: Long?): Long {
    val limit = budgetClass.singleFileLimit()
    val reservedBytes = declaredBytes ?: limit
    if (reservedBytes !in 0..limit) failBudget("资产超过单文件预算")
    return reservedBytes
}

private fun validCurrentBytes(currentBytes: Long): Long {
    if (currentBytes !in 0..AssetBudgetLimits.PEAK_BYTES) failBudget("current 资产预算无效")
    return currentBytes
}

internal fun utf8ByteCount(value: String): Long {
    var bytes = 0L
    var index = 0
    while (index < value.length) {
        val code = value[index].code
        when {
            code <= 0x7F -> bytes += 1
            code <= 0x7FF -> bytes += 2
            code in 0xD800..0xDBFF -> {
                if (index + 1 >= value.length || value[index + 1].code !in 0xDC00..0xDFFF) {
                    failBudget("文本包含无效 Unicode 代理项")
                }
                bytes += 4
                index += 1
            }
            code in 0xDC00..0xDFFF -> failBudget("文本包含无效 Unicode 代理项")
            else -> bytes += 3
        }
        index += 1
    }
    return bytes
}

private fun AssetBudgetClass.singleFileLimit(): Long = when (this) {
    AssetBudgetClass.TEXT -> AssetBudgetLimits.TEXT_BYTES
    AssetBudgetClass.AICR,
    AssetBudgetClass.BBMODEL,
    AssetBudgetClass.IMAGE,
    -> AssetBudgetLimits.BINARY_BYTES
}

private fun failBudget(message: String): Nothing = throw AssetPolicyException(message)
